import axios from "axios";
import { getToken, getRefreshToken, setTokens, clearToken, tokenExpiry, BASE, getPlatformToken, clearPlatformToken } from "./auth";

const REFRESH_URL = process.env.REACT_APP_REFRESH_URL;

// Refresh this long before the access token actually expires, so a request is
// never sent with a token that dies in flight.
const SKEW_MS = 60000;

const api = axios.create({ baseURL: process.env.REACT_APP_API_BASE || "http://localhost:7250/dpdp" });

// Single-flight: many parallel requests hitting an expired token must trigger
// ONE refresh, not one each — the upstream rotates the refresh token on every
// call, so concurrent refreshes would invalidate each other.
let inFlight = null;

function logout() {
  clearToken();
  if (!window.location.pathname.endsWith("/login")) window.location.assign(`${BASE}/login`);
}

function refreshAccessToken() {
  const rt = getRefreshToken();
  if (!rt || !REFRESH_URL) return Promise.resolve(null);

  if (!inFlight) {
    inFlight = axios
      .post(REFRESH_URL, { refresh_token: rt })
      .then((r) => {
        const d = r?.data?.data || r?.data || {};
        const access = d.access_token || d.accessToken;
        if (!access) return null;
        setTokens(access, d.refresh_token || d.refreshToken);
        return access;
      })
      .catch(() => null)
      .finally(() => {
        inFlight = null;
      });
  }
  return inFlight;
}

// Proactive: swap a near-dead token before the request goes out.
api.interceptors.request.use(async (cfg) => {
  let t = getToken();
  if (t && getRefreshToken()) {
    const exp = tokenExpiry(t);
    if (exp && Date.now() > exp - SKEW_MS) {
      const fresh = await refreshAccessToken();
      if (fresh) t = fresh;
    }
  }
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

// Reactive: a 401 still gets one refresh + replay before we give up. `_retried`
// guards against a loop when the new token is rejected too.
api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const cfg = err?.config;
    if (err?.response?.status === 401 && cfg && !cfg._retried) {
      cfg._retried = true;
      const fresh = await refreshAccessToken();
      if (fresh) {
        cfg.headers = { ...(cfg.headers || {}), Authorization: `Bearer ${fresh}` };
        return api(cfg);
      }
      logout();
    }
    return Promise.reject(err);
  }
);

export default api;

// Platform console client (/admin): same API, platform token, no refresh flow.
export const platformApi = axios.create({ baseURL: api.defaults.baseURL });
platformApi.interceptors.request.use((cfg) => {
  const t = getPlatformToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});
platformApi.interceptors.response.use((r) => r, (err) => {
  if (err?.response?.status === 401) { clearPlatformToken(); window.location.assign("/admin/login"); }
  return Promise.reject(err);
});
